import { useContext, useEffect, useState } from "react";
import PasswordResetModel from "pages/Models/passwordResetModel";
import { UserDataContext } from "context";
import EditUserNameModel from "pages/Models/editUserNameModel";
import TailwindLayout from "TailwindcssComponents/TailwindLayout";
import TopBar from "TailwindcssComponents/TopBar";
import FooterTailwind from "TailwindcssComponents/Footer";
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { Icon } from "@iconify/react";
import axios from "axios";

function Overview() {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const { userData, fetchUser } = useContext(UserDataContext);

  const userRole = userData?.isAdmin ? 'admin' : userData?.userRoles?.[0]?.name || 'user';
  const orgId = userData?.userRoles?.[0]?.orgId || null;

  const profile = {
    firstName: userData?.firstName || 'First Name',
    lastName: userData?.lastName || 'Last Name',
    email: userData?.email || 'Email',
    phone: userData?.phone || 'Phone',
    city: userData?.city || 'City',
    country: userData?.country || 'Country',
    profileImage: userData?.logo?.secure_url
  };

  const [openResetModel, setOpenResetModel] = useState(false);
  const [openUserNameModel, setOpenUserNameModel] = useState(false);
  const [eventsData, setEventsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleUserUpdate = () => fetchUser();

  const fetchEvents = async () => {
    if (userRole === 'admin' || !orgId) return; // Prevent fetching if orgId is not available
    try {
      setIsLoading(true);
      const { data } = await axios.get(`${apiUrl}event-service/event`, { params: { orgId } });
      if (data?.success) setEventsData(data.data);
      else throw new Error(data?.message || 'Error fetching events');
    } catch (error) {
      console.error(error.response?.data?.message || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (orgId) fetchEvents(); // Fetch events only when orgId is available
  }, [orgId]);

  return (
    <TailwindLayout>
      <TopBar />

      <div className="min-h-screen pb-20 bg-gray-100 p-6">
        <div className="relative h-48 rounded-lg overflow-hidden">
          <div className="absolute inset-0 bg-blue-600"></div>
          <div className="absolute inset-0">
            <img
              src="/logoSeatsnaps.png"
              className="w-full h-full object-cover opacity-40"
              alt="Background"
            />
          </div>
        </div>
        {/* Profile Card */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative -mt-24">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                {/* Profile Image */}
                <img src={profile.profileImage} alt="Profile" className="rounded-full w-24 h-24 border-4 border-white shadow-md" />

                {/* Profile Info */}
                <div className="flex-grow">
                  <h1 className="text-2xl font-bold text-gray-900">{profile.firstName} {profile.lastName}</h1>
                  <div className="mt-1 text-sm text-gray-600 space-x-2">
                    <span>{userRole}</span>
                    <span>•</span>
                    <span>{profile.email}</span>
                    <span>•</span>
                    <span>{profile.phone}</span>
                    <span>•</span>
                    <span>{profile.city}, {profile.country}</span>
                  </div>
                </div>

                {/* Settings Button */}
                <Menu as="div" className="relative inline-block text-left">
                  <MenuButton className="inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 hover:bg-gray-50">
                    <Icon icon="mingcute:user-edit-line" className="text-xl" />
                    Edit
                    <Icon icon="iconamoon:arrow-down-2-thin" className="text-xl" />
                  </MenuButton>
                  <MenuItems className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white ring-1 shadow-lg ring-black/5">
                    <div className="py-1">
                      <MenuItem>
                        {({ active }) => (
                          <button onClick={() => setOpenResetModel(true)} className={`block w-full text-left px-4 py-2 text-sm ${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'}`}>
                            Change Password
                          </button>
                        )}
                      </MenuItem>
                      <MenuItem>
                        {({ active }) => (
                          <button onClick={() => setOpenUserNameModel(true)} className={`block w-full text-left px-4 py-2 text-sm ${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'}`}>
                            Edit Profile
                          </button>
                        )}
                      </MenuItem>
                    </div>
                  </MenuItems>
                </Menu>
              </div>
            </div>
          </div>
          {/* Events Section */}
          {!isLoading && eventsData.length > 0 && (
            <div className="mt-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Events</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {eventsData.map(event => (
                  <div key={event.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                    <img src={event.coverImage?.secure_url || "/api/placeholder/400/200"} alt={event.name} className="w-full h-48 object-cover" />
                    <div className="p-4">
                      <span className="inline-block px-2 py-1 text-xs font-semibold text-blue-600 bg-blue-100 rounded-full">{event.category?.name}</span>
                      <h3 className="mt-2 text-lg font-semibold text-gray-900">{event.name}</h3>
                      <p className="mt-2 text-sm text-gray-600">{event.description?.replace(/<[^>]*>/g, '').substring(0, 50)}...</p>
                      <button className="mt-4 w-full px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700" onClick={() => window.location.href = `/events/single-event/${event.id}`}>
                        View Event
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <PasswordResetModel
        open={openResetModel}
        onClose={() => setOpenResetModel(false)}
      />

      <EditUserNameModel
        open={openUserNameModel}
        onClose={() => setOpenUserNameModel(false)}
        propFirstName={profile.firstName}  // Ensure proper naming
        propLastName={profile.lastName}
        propCity={profile.city}
        propCountry={profile.country}
        propImage={profile.profileImage}
        onUserUpdate={handleUserUpdate}
      />

      <FooterTailwind />
    </TailwindLayout>
  );
}

export default Overview;
